import { useState, useCallback, useEffect, useRef } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { settingsApi, adminApi } from "@/services/api";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface MediaFile {
  id: string;
  name: string;
  url: string;
  size: string;
  type: string;
  category: string;
  uploadedAt: string;
  isLocal?: boolean;
}

interface PendingFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: string;
  type: string;
  category: string;
  compressionSaved?: number;
}

// ── Per-category compression settings ────────────────────────────────────────
interface CompressionConfig {
  quality: number;
  maxWidthPx: number;
  enabled: boolean;
}

const DEFAULT_COMPRESSION: Record<string, CompressionConfig> = {
  logos:       { quality: 0.95, maxWidthPx: 1200, enabled: true  },
  favicons:    { quality: 0.95, maxWidthPx: 256,  enabled: false },
  job_photos:  { quality: 0.72, maxWidthPx: 1920, enabled: true  },
  site_visits: { quality: 0.72, maxWidthPx: 1920, enabled: true  },
  avatars:     { quality: 0.85, maxWidthPx: 400,  enabled: true  },
  documents:   { quality: 1.0,  maxWidthPx: 9999, enabled: false },
};

// ── Image Compression ─────────────────────────────────────────────────────────
async function compressImage(file: File, cfg: CompressionConfig): Promise<File> {
  if (!cfg.enabled || !file.type.startsWith("image/") || file.type === "image/svg+xml") return file;
  return new Promise<File>((resolve) => {
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let { width, height } = img;
      if (width > cfg.maxWidthPx) {
        height = Math.round((height / width) * cfg.maxWidthPx);
        width  = cfg.maxWidthPx;
      }
      const canvas = document.createElement("canvas");
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);
      const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        const ext = mime === "image/jpeg" ? ".jpg" : ".png";
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, "") + ext, { type: mime }));
      }, mime, cfg.quality);
    };
    img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(file); };
    img.src = blobUrl;
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────
type MediaCategory = "all" | "logos" | "favicons" | "job_photos" | "site_visits" | "avatars" | "documents";

const CATEGORY_CONFIG: Record<string, { label: string; icon: string; color: string; bg: string; accept: string; maxMB: number }> = {
  logos:       { label: "Logos",            icon: "ri-image-line",      color: "#7c3aed", bg: "#f5f3ff", accept: "image/*,.svg",               maxMB: 2  },
  favicons:    { label: "Favicons",          icon: "ri-bookmark-line",   color: "#f59e0b", bg: "#fffbeb", accept: "image/*,.ico",               maxMB: 1  },
  job_photos:  { label: "Job Card Photos",   icon: "ri-camera-line",     color: "#10b981", bg: "#ecfdf5", accept: "image/*",                    maxMB: 10 },
  site_visits: { label: "Site Visit Photos", icon: "ri-map-pin-2-line",  color: "#3b82f6", bg: "#eff6ff", accept: "image/*",                    maxMB: 10 },
  avatars:     { label: "Avatars",           icon: "ri-user-3-line",     color: "#ef4444", bg: "#fef2f2", accept: "image/*",                    maxMB: 2  },
  documents:   { label: "Documents",         icon: "ri-file-text-line",  color: "#64748b", bg: "#f8fafc", accept: ".pdf,.doc,.docx,.xlsx,.zip", maxMB: 20 },
};

const SEED_MEDIA: MediaFile[] = [
  { id: "m1",  name: "cadsoft-logo.svg",     url: "https://storage.readdy-site.link/project_files/5879db97-9e94-48bf-be04-c174ef541633/497af63a-0e64-4809-85b5-1474aa7321a9_icon-cadsoft-logo.svg?v=8bf3bb090434ec9bacdcf0e4de437e93", size: "12 KB",  type: "image/svg+xml",  category: "logos",       uploadedAt: "2026-01-10T09:00:00Z" },
  { id: "m2",  name: "site-favicon.png",      url: "https://readdy.ai/api/search-image?query=small%20square%20favicon%20icon%20for%20survey%20engineering%20company%20minimal%20geometric%20design%20teal%20green%20on%20white%20background%20clean%20professional&width=64&height=64&seq=fav1&orientation=squarish", size: "4 KB",   type: "image/png",      category: "favicons",    uploadedAt: "2026-01-10T09:05:00Z" },
  { id: "m3",  name: "job_SURV001_site1.jpg", url: "https://readdy.ai/api/search-image?query=land%20survey%20field%20work%20team%20with%20total%20station%20equipment%20on%20open%20terrain%20sunny%20day%20professional%20photography%20wide%20angle%20shot&width=800&height=600&seq=job1&orientation=landscape", size: "1.2 MB", type: "image/jpeg",     category: "job_photos",  uploadedAt: "2026-02-14T11:30:00Z" },
  { id: "m4",  name: "job_SURV001_site2.jpg", url: "https://readdy.ai/api/search-image?query=surveying%20equipment%20GPS%20rover%20on%20tripod%20in%20field%20with%20construction%20site%20background%20clear%20sky%20professional&width=800&height=600&seq=job2&orientation=landscape", size: "980 KB", type: "image/jpeg",     category: "job_photos",  uploadedAt: "2026-02-14T11:45:00Z" },
  { id: "m5",  name: "job_SURV003_drone.jpg", url: "https://readdy.ai/api/search-image?query=aerial%20drone%20photography%20of%20land%20parcel%20boundary%20survey%20markers%20visible%20from%20above%20green%20fields%20India&width=800&height=600&seq=job3&orientation=landscape", size: "2.1 MB", type: "image/jpeg",     category: "job_photos",  uploadedAt: "2026-03-02T14:00:00Z" },
  { id: "m6",  name: "visit_HYD_001.jpg",     url: "https://readdy.ai/api/search-image?query=site%20visit%20inspection%20engineer%20with%20clipboard%20at%20construction%20site%20Hyderabad%20India%20professional%20photography&width=800&height=600&seq=sv1&orientation=landscape", size: "1.5 MB", type: "image/jpeg",     category: "site_visits", uploadedAt: "2026-03-15T10:00:00Z" },
  { id: "m7",  name: "visit_HYD_002.jpg",     url: "https://readdy.ai/api/search-image?query=site%20visit%20photo%20boundary%20wall%20measurement%20tape%20engineer%20outdoor%20India%20urban%20area&width=800&height=600&seq=sv2&orientation=landscape", size: "1.1 MB", type: "image/jpeg",     category: "site_visits", uploadedAt: "2026-03-15T10:30:00Z" },
  { id: "m8",  name: "avatar_rajesh.jpg",      url: "https://readdy.ai/api/search-image?query=professional%20headshot%20Indian%20male%20engineer%2040s%20confident%20smile%20office%20background%20clean%20portrait&width=200&height=200&seq=av1&orientation=squarish", size: "85 KB",  type: "image/jpeg",     category: "avatars",     uploadedAt: "2026-01-05T08:00:00Z" },
  { id: "m9",  name: "avatar_priya.jpg",       url: "https://readdy.ai/api/search-image?query=professional%20headshot%20Indian%20female%20manager%2030s%20confident%20smile%20office%20background%20clean%20portrait&width=200&height=200&seq=av2&orientation=squarish", size: "78 KB",  type: "image/jpeg",     category: "avatars",     uploadedAt: "2026-01-05T08:10:00Z" },
  { id: "m10", name: "survey_report_Q1.pdf",   url: "#", size: "3.4 MB", type: "application/pdf", category: "documents",   uploadedAt: "2026-04-01T09:00:00Z" },
  { id: "m11", name: "job_SURV005_photo.jpg",  url: "https://readdy.ai/api/search-image?query=topographic%20survey%20team%20measuring%20elevation%20with%20level%20instrument%20rural%20India%20farmland%20background&width=800&height=600&seq=job5&orientation=landscape", size: "1.8 MB", type: "image/jpeg",     category: "job_photos",  uploadedAt: "2026-03-28T13:00:00Z" },
  { id: "m12", name: "visit_SEC_003.jpg",      url: "https://readdy.ai/api/search-image?query=site%20visit%20photo%20engineer%20measuring%20plot%20dimensions%20with%20measuring%20tape%20residential%20area%20India&width=800&height=600&seq=sv3&orientation=landscape", size: "1.3 MB", type: "image/jpeg",     category: "site_visits", uploadedAt: "2026-04-02T11:00:00Z" },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Image Card (native drag-and-drop) ────────────────────────────────────────
interface ImageCardProps {
  file: MediaFile;
  onPreview: (f: MediaFile) => void;
  onDelete: (id: string) => void;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  selectionMode: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: (id: string) => void;
  onDragOver: (id: string) => void;
  onDrop: (id: string) => void;
  onDragEnd: () => void;
}

function ImageCard({
  file, onPreview, onDelete, isSelected, onToggleSelect, selectionMode,
  isDragging, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd,
}: ImageCardProps) {
  const catCfg = CATEGORY_CONFIG[file.category];

  return (
    <div
      draggable={!selectionMode}
      onDragStart={() => onDragStart(file.id)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(file.id); }}
      onDrop={() => onDrop(file.id)}
      onDragEnd={onDragEnd}
      style={{ aspectRatio: "1", opacity: isDragging ? 0.35 : 1 }}
      className={`relative group rounded-xl overflow-hidden border bg-gray-50 transition-all ${
        isDragOver && !isDragging
          ? "border-violet-400 ring-2 ring-violet-300 scale-105"
          : isSelected
          ? "border-violet-400 ring-2 ring-violet-300"
          : "border-gray-100 hover:border-gray-200"
      }`}
    >
      {/* Selection checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect(file.id); }}
        className={`absolute top-1.5 left-1.5 z-10 w-5 h-5 flex items-center justify-center rounded-md border-2 cursor-pointer transition-all ${
          isSelected
            ? "bg-violet-500 border-violet-500"
            : selectionMode
            ? "bg-white/90 border-gray-300"
            : "bg-white/90 border-gray-300 opacity-0 group-hover:opacity-100"
        }`}
      >
        {isSelected && <i className="ri-check-line text-white text-xs" />}
      </button>

      {/* Drag handle indicator */}
      {!selectionMode && (
        <div
          className="absolute top-1.5 right-1.5 z-10 w-6 h-6 flex items-center justify-center bg-black/40 rounded-md cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
          title="Drag to reorder"
        >
          <i className="ri-drag-move-2-line text-white text-xs" />
        </div>
      )}

      <img
        src={file.url}
        alt={file.name}
        className="w-full h-full object-cover cursor-pointer"
        onClick={() => selectionMode ? onToggleSelect(file.id) : onPreview(file)}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%239ca3af' font-size='12'%3ENo Preview%3C/text%3E%3C/svg%3E";
        }}
      />

      {/* Hover overlay */}
      {!selectionMode && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
          <button onClick={(e) => { e.stopPropagation(); onPreview(file); }}
            className="w-7 h-7 flex items-center justify-center bg-white/90 rounded-lg cursor-pointer hover:bg-white transition-all">
            <i className="ri-zoom-in-line text-gray-700 text-sm" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(file.id); }}
            className="w-7 h-7 flex items-center justify-center bg-red-500/90 rounded-lg cursor-pointer hover:bg-red-600 transition-all">
            <i className="ri-delete-bin-line text-white text-sm" />
          </button>
        </div>
      )}

      {/* Category badge */}
      {catCfg && (
        <div className="absolute bottom-1.5 left-1.5">
          <span className="text-xs px-1.5 py-0.5 rounded-md font-medium backdrop-blur-sm"
            style={{ background: catCfg.color + "cc", color: "#fff" }}>
            {catCfg.label.split(" ")[0]}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Compression Panel ────────────────────────────────────────────────────────
interface CompressionPanelProps {
  compressionCfg: Record<string, CompressionConfig>;
  onChange: (key: string, field: keyof CompressionConfig, val: number | boolean) => void;
  uploadCategory: string;
}

function CompressionPanel({ compressionCfg, onChange, uploadCategory }: CompressionPanelProps) {
  const cfg = compressionCfg[uploadCategory];
  if (!cfg) return null;
  const catCfg = CATEGORY_CONFIG[uploadCategory];
  const isImageCat = uploadCategory !== "documents";

  return (
    <div className="border border-gray-100 rounded-xl p-4 bg-gray-50 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-settings-3-line text-gray-500" />
          </span>
          Compression Settings — {catCfg?.label}
        </p>
        {isImageCat && (
          <label className="flex items-center gap-1.5 cursor-pointer">
            <div
              onClick={() => onChange(uploadCategory, "enabled", !cfg.enabled)}
              className={`w-8 h-4 rounded-full transition-all flex items-center px-0.5 ${cfg.enabled ? "bg-violet-500" : "bg-gray-300"}`}
            >
              <div className={`w-3 h-3 bg-white rounded-full transition-all ${cfg.enabled ? "translate-x-4" : "translate-x-0"}`} />
            </div>
            <span className="text-xs text-gray-600">{cfg.enabled ? "Enabled" : "Disabled"}</span>
          </label>
        )}
      </div>

      {isImageCat && cfg.enabled && (
        <div className="space-y-3">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-xs text-gray-600">Quality</span>
              <span className="text-xs font-semibold" style={{ color: "#7c3aed" }}>
                {Math.round(cfg.quality * 100)}%
                <span className="text-gray-400 font-normal ml-1">
                  {cfg.quality >= 0.9 ? "(Max — best for logos)" : cfg.quality >= 0.75 ? "(High)" : cfg.quality >= 0.6 ? "(Medium — good for photos)" : "(Low)"}
                </span>
              </span>
            </div>
            <input
              type="range" min={30} max={100} step={5}
              value={Math.round(cfg.quality * 100)}
              onChange={(e) => onChange(uploadCategory, "quality", Number(e.target.value) / 100)}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: "#7c3aed" }}
            />
            <div className="flex justify-between text-xs text-gray-400 mt-0.5">
              <span>30% (Small file)</span>
              <span>100% (Original)</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1.5">
              <span className="text-xs text-gray-600">Max Width</span>
              <span className="text-xs font-semibold text-gray-700">{cfg.maxWidthPx >= 9999 ? "Unlimited" : `${cfg.maxWidthPx}px`}</span>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[256, 400, 800, 1200, 1920, 9999].map((w) => (
                <button key={w}
                  onClick={() => onChange(uploadCategory, "maxWidthPx", w)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                    cfg.maxWidthPx === w ? "text-white" : "border border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  style={cfg.maxWidthPx === w ? { background: "#7c3aed" } : {}}>
                  {w >= 9999 ? "Unlimited" : `${w}px`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {(!isImageCat || !cfg.enabled) && (
        <p className="text-xs text-gray-400">
          {!isImageCat
            ? "Documents are never compressed — original files are kept."
            : "Compression disabled — files will be uploaded at original quality."}
        </p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminMediaPage() {
  const [activeCategory, setActiveCategory]  = useState<MediaCategory>("all");
  const [search,         setSearch]          = useState("");
  const [mediaFiles,     setMediaFiles]      = useState<MediaFile[]>(SEED_MEDIA);
  const [loadingMedia,   setLoadingMedia]    = useState(true);
  const [serverConnected,setServerConnected] = useState<boolean | null>(null);

  // Upload state
  const [uploading,       setUploading]       = useState(false);
  const [uploadProgress,  setUploadProgress]  = useState(0);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadCategory,  setUploadCategory]  = useState<string>("job_photos");
  const [pendingFiles,    setPendingFiles]    = useState<PendingFile[]>([]);
  const [showCompression, setShowCompression] = useState(false);
  const [compressionCfg,  setCompressionCfg] = useState<Record<string, CompressionConfig>>({ ...DEFAULT_COMPRESSION });

  // Lightbox
  const [lightbox, setLightbox] = useState<MediaFile | null>(null);

  // Sort
  const [sortBy, setSortBy] = useState<"date" | "name">("date");

  // Bulk selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds,   setSelectedIds]   = useState<Set<string>>(new Set());
  const [bulkDeleting,  setBulkDeleting]  = useState(false);
  const [bulkConfirm,   setBulkConfirm]   = useState(false);

  // Native drag-and-drop state
  const [dragId,     setDragId]     = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" | "info" } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = useCallback((msg: string, type: "success" | "error" | "info" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Load from server on mount
  useEffect(() => {
    setLoadingMedia(true);
    adminApi
      .getMedia({ limit: 200 } as Parameters<typeof adminApi.getMedia>[0])
      .then((res: { data?: Record<string, unknown>[] }) => {
        const rows = res?.data ?? [];
        setServerConnected(true);
        if (rows.length > 0) {
          setMediaFiles(rows.map((r) => ({
            id: String(r.id),
            name: String(r.original_name || r.name || ""),
            url: String(r.url),
            size: String(r.size || "—"),
            type: String(r.type || "application/octet-stream"),
            category: String(r.category || "documents"),
            uploadedAt: String(r.uploaded_at || r.created_at || new Date().toISOString()),
          })));
        }
      })
      .catch(() => setServerConnected(false))
      .finally(() => setLoadingMedia(false));
  }, []);

  // ── Compression config helpers ────────────────────────────────────────────
  const updateCompression = useCallback((key: string, field: keyof CompressionConfig, val: number | boolean) => {
    setCompressionCfg((prev) => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  }, []);

  // ── File selection with compression ──────────────────────────────────────
  const handleFilesSelected = useCallback(async (fileList: FileList) => {
    const catCfg = CATEGORY_CONFIG[uploadCategory];
    const maxMB  = catCfg?.maxMB ?? 10;
    const errs: string[] = [];
    const valid: File[]  = [];

    Array.from(fileList).forEach((f) => {
      if (f.size > maxMB * 1024 * 1024) { errs.push(`"${f.name}" exceeds ${maxMB} MB`); return; }
      valid.push(f);
    });

    if (errs.length) showToast(errs.join(" | "), "error");
    if (!valid.length) return;

    const cfg = compressionCfg[uploadCategory] ?? DEFAULT_COMPRESSION[uploadCategory] ?? { quality: 0.8, maxWidthPx: 1920, enabled: true };
    const compressed = await Promise.all(valid.map((f) => compressImage(f, cfg)));

    compressed.forEach((file, idx) => {
      const savedKB = Math.round((valid[idx].size - file.size) / 1024);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPendingFiles((prev) => [
          ...prev,
          {
            id: `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            file, preview: e.target?.result as string,
            name: file.name, size: formatBytes(file.size),
            type: file.type, category: uploadCategory,
            compressionSaved: savedKB > 0 ? savedKB : 0,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  }, [uploadCategory, compressionCfg, showToast]);

  const handleDropZone = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) handleFilesSelected(e.dataTransfer.files);
  }, [handleFilesSelected]);

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleSaveUploads = async () => {
    if (!pendingFiles.length) return;
    setUploading(true); setUploadProgress(0);
    const saved: MediaFile[] = [];
    let serverErrorCount = 0;
    
    for (let i = 0; i < pendingFiles.length; i++) {
      const pf = pendingFiles[i];
      try {
        const fd = new FormData();
        fd.append("file", pf.file, pf.name);
        fd.append("category", pf.category);
        const result = await settingsApi.uploadMediaFile(fd);
        // Server returns the saved file record
        saved.push({ 
          id: String(result?.id || `uploaded_${Date.now()}_${i}`), 
          name: result?.original_name || pf.name, 
          url: result?.url || pf.preview, 
          size: result?.size || pf.size, 
          type: result?.type || pf.type, 
          category: result?.category || pf.category, 
          uploadedAt: result?.uploaded_at || new Date().toISOString(), 
          isLocal: false 
        });
      } catch (err) {
        serverErrorCount++;
        // Don't add to gallery on error - just track the error
        console.error("Upload failed:", err);
      }
      setUploadProgress(Math.round(((i + 1) / pendingFiles.length) * 100));
    }
    
    // Only add successfully uploaded files to the gallery
    if (saved.length > 0) {
      setMediaFiles((prev) => [...saved, ...prev]);
    }
    
    setPendingFiles([]); 
    setUploading(false); 
    setUploadProgress(0);
    
    // Show appropriate toast
    if (serverErrorCount > 0 && saved.length === 0) {
      showToast(`Server unavailable — ${serverErrorCount} file(s) failed to upload`, "error");
      setServerConnected(false);
    } else if (serverErrorCount > 0) {
      showToast(`${saved.length} uploaded, ${serverErrorCount} failed`, "info");
    } else {
      showToast(`${saved.length} file(s) uploaded successfully`);
    }
  };

  // ── Delete single ─────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (id: string) => {
    try { await adminApi.deleteMedia(id); } catch { /* offline */ }
    setMediaFiles((prev) => prev.filter((f) => f.id !== id));
    setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    if (lightbox?.id === id) setLightbox(null);
    showToast("File deleted");
  }, [lightbox, showToast]);

  // ── Bulk selection helpers ────────────────────────────────────────────────
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    await Promise.all(ids.map((id) => adminApi.deleteMedia(id).catch(() => null)));
    setMediaFiles((prev) => prev.filter((f) => !selectedIds.has(f.id)));
    if (lightbox && selectedIds.has(lightbox.id)) setLightbox(null);
    setBulkConfirm(false);
    setSelectionMode(false);
    setSelectedIds(new Set());
    setBulkDeleting(false);
    showToast(`${ids.length} file(s) deleted`);
  };

  // ── Native drag-and-drop reorder ─────────────────────────────────────────
  const handleDragStart = useCallback((id: string) => {
    setDragId(id);
  }, []);

  const handleDragOver = useCallback((id: string) => {
    if (id !== dragId) setDragOverId(id);
  }, [dragId]);

  const handleDrop = useCallback((targetId: string) => {
    if (!dragId || dragId === targetId) return;
    setMediaFiles((prev) => {
      const arr = [...prev];
      const fromIdx = arr.findIndex((f) => f.id === dragId);
      const toIdx   = arr.findIndex((f) => f.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
    setDragId(null);
    setDragOverId(null);
  }, [dragId]);

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setDragOverId(null);
  }, []);

  // ── Filtered + sorted list ────────────────────────────────────────────────
  const filtered = mediaFiles
    .filter((f) => {
      if (activeCategory !== "all" && f.category !== activeCategory) return false;
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => sortBy === "date"
      ? new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      : a.name.localeCompare(b.name));

  const imageFiles = filtered.filter((f) => f.type.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.url));
  const docFiles   = filtered.filter((f) => !imageFiles.includes(f));
  const categoryCounts = Object.keys(CATEGORY_CONFIG).reduce(
    (acc, key) => ({ ...acc, [key]: mediaFiles.filter((f) => f.category === key).length }),
    {} as Record<string, number>
  );

  return (
    <ERPLayout title="Media Library">

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 text-white text-sm px-4 py-2.5 rounded-xl font-medium flex items-center gap-2"
          style={{ background: toast.type === "success" ? "#7c3aed" : toast.type === "info" ? "#3b82f6" : "#ef4444" }}>
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : toast.type === "info" ? "ri-information-line" : "ri-error-warning-line"} />
          {toast.msg}
        </div>
      )}

      {/* Bulk Delete Confirm */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <i className="ri-delete-bin-2-line text-2xl text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Delete {selectedIds.size} file(s)?</h3>
            <p className="text-sm text-gray-500 mb-5">This will permanently remove the selected files. This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setBulkConfirm(false)}
                className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-bold cursor-pointer hover:bg-red-600 transition-all whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2">
                {bulkDeleting
                  ? <><i className="ri-loader-4-line animate-spin" />Deleting...</>
                  : <><i className="ri-delete-bin-2-line" />Delete {selectedIds.size}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)}
              className="absolute -top-10 right-0 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
              <i className="ri-close-line text-xl" />
            </button>
            <img src={lightbox.url} alt={lightbox.name} className="w-full max-h-[80vh] object-contain rounded-xl"
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23111'/%3E%3Ctext x='200' y='155' text-anchor='middle' fill='%23666' font-size='16'%3EImage not available%3C/text%3E%3C/svg%3E"; }} />
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-semibold">{lightbox.name}</p>
                <p className="text-white/50 text-xs">{lightbox.size} · {CATEGORY_CONFIG[lightbox.category]?.label || lightbox.category} · {new Date(lightbox.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                <p className="text-white/30 text-xs mt-0.5 truncate max-w-md">{lightbox.url}</p>
              </div>
              <div className="flex gap-2">
                <a href={lightbox.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap">
                  <i className="ri-external-link-line" />Open
                </a>
                <a href={lightbox.url} download={lightbox.name}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap">
                  <i className="ri-download-line" />Download
                </a>
                <button onClick={() => handleDelete(lightbox.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/80 hover:bg-red-600 text-white rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap">
                  <i className="ri-delete-bin-line" />Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 space-y-5">

        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm" />
              </span>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search files..."
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl w-56 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400" />
            </div>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as "date" | "name")}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none cursor-pointer">
              <option value="date">Newest First</option>
              <option value="name">Name A–Z</option>
            </select>

            {/* Server badge */}
            {serverConnected !== null && (
              <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium ${serverConnected ? "bg-amber-50 text-amber-700" : "bg-amber-50 text-amber-700"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${serverConnected ? "bg-amber-500" : "bg-amber-400"}`} />
                {serverConnected ? "Server connected" : "Showing demo data"}
              </div>
            )}

            {/* Bulk select toggle */}
            <button
              onClick={() => { setSelectionMode((v) => !v); setSelectedIds(new Set()); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap border ${
                selectionMode ? "border-violet-400 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-checkbox-multiple-line text-sm" />
              </span>
              {selectionMode ? "Exit Select" : "Select"}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Bulk action bar */}
            {selectionMode && selectedIds.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-xl">
                <span className="text-xs font-semibold text-violet-700">{selectedIds.size} selected</span>
                <button onClick={() => setSelectedIds(new Set(filtered.map((f) => f.id)))}
                  className="text-xs text-violet-600 hover:underline cursor-pointer whitespace-nowrap">Select all</button>
                <button onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-gray-500 hover:underline cursor-pointer whitespace-nowrap">Clear</button>
                <button onClick={() => setBulkConfirm(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-white px-2.5 py-1.5 rounded-lg cursor-pointer whitespace-nowrap bg-red-500 hover:bg-red-600 transition-all">
                  <i className="ri-delete-bin-line" />Delete {selectedIds.size}
                </button>
              </div>
            )}

            <button onClick={() => setShowUploadPanel(!showUploadPanel)}
              className="flex items-center gap-2 text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap"
              style={{ background: "#7c3aed" }}>
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line text-sm" /></span>
              Upload Media
            </button>
          </div>
        </div>

        {/* ── Upload Panel ─────────────────────────────────────────────────── */}
        {showUploadPanel && (
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Upload New Media</h3>
              <button onClick={() => { setShowUploadPanel(false); setPendingFiles([]); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {/* Category chips */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Upload Category</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <button key={key} onClick={() => setUploadCategory(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                      uploadCategory === key ? "border-violet-400 text-white" : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                    style={uploadCategory === key ? { background: "#7c3aed" } : {}}>
                    <i className={cfg.icon} />{cfg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Compression settings toggle */}
            <button
              onClick={() => setShowCompression((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 cursor-pointer transition-all"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-settings-3-line text-sm ${showCompression ? "text-violet-500" : ""}`} />
              </span>
              Compression Settings
              {showCompression ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
              {compressionCfg[uploadCategory]?.enabled && uploadCategory !== "documents" && (
                <span className="ml-1 text-xs bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">
                  {Math.round(compressionCfg[uploadCategory].quality * 100)}% quality · max {
                    compressionCfg[uploadCategory].maxWidthPx >= 9999
                      ? "∞"
                      : `${compressionCfg[uploadCategory].maxWidthPx}px`
                  }
                </span>
              )}
            </button>
            {showCompression && (
              <CompressionPanel
                compressionCfg={compressionCfg}
                onChange={updateCompression}
                uploadCategory={uploadCategory}
              />
            )}

            {/* Drop zone */}
            <div
              className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all hover:border-violet-300 hover:bg-violet-50/20 border-gray-200"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDropZone}
              onDragOver={(e) => e.preventDefault()}
            >
              <input ref={fileInputRef} type="file"
                accept={CATEGORY_CONFIG[uploadCategory]?.accept || "image/*"} multiple className="hidden"
                onChange={(e) => { if (e.target.files?.length) { handleFilesSelected(e.target.files); e.target.value = ""; } }} />
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-violet-50 mx-auto mb-3">
                <i className="ri-upload-cloud-2-line text-3xl" style={{ color: "#7c3aed" }} />
              </div>
              <p className="text-sm font-semibold text-gray-700">Click or drag &amp; drop files here</p>
              <p className="text-xs text-gray-400 mt-1">
                {CATEGORY_CONFIG[uploadCategory]?.accept?.replace(/image\/\*/g, "Images").replace(/,/g, ", ")} · Max {CATEGORY_CONFIG[uploadCategory]?.maxMB} MB per file
              </p>
            </div>

            {/* Pending previews */}
            {pendingFiles.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  Ready to upload ({pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""})
                  {pendingFiles.some((f) => (f.compressionSaved ?? 0) > 0) && (
                    <span className="ml-2 text-amber-600">
                      · Saved ~{pendingFiles.reduce((a, f) => a + (f.compressionSaved ?? 0), 0)} KB via compression
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                  {pendingFiles.map((pf) => (
                    <div key={pf.id} className="relative group rounded-lg overflow-hidden border border-gray-100 bg-gray-50" style={{ aspectRatio: "1" }}>
                      {pf.type.startsWith("image/") ? (
                        <img src={pf.preview} alt={pf.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                          <i className="ri-file-text-line text-2xl text-gray-400" />
                          <span className="text-xs text-gray-400 truncate px-1 w-full text-center">{pf.name.slice(-8)}</span>
                        </div>
                      )}
                      <button onClick={() => setPendingFiles((prev) => prev.filter((x) => x.id !== pf.id))}
                        className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <i className="ri-close-line text-white text-xs" />
                      </button>
                      {(pf.compressionSaved ?? 0) > 0 && (
                        <div className="absolute top-0.5 left-0.5 bg-amber-500 text-white text-xs px-1 py-0.5 rounded-md leading-none font-semibold">
                          -{pf.compressionSaved}KB
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1 py-0.5 truncate text-center">
                        {pf.size}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploading && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 font-medium">Uploading…</span>
                  <span className="text-violet-600 font-semibold">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%`, background: "#7c3aed" }} />
                </div>
              </div>
            )}

            {pendingFiles.length > 0 && !uploading && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-600"><span className="font-semibold text-gray-800">{pendingFiles.length}</span> file(s) ready</p>
                <div className="flex gap-2">
                  <button onClick={() => setPendingFiles([])}
                    className="px-3 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
                    Clear All
                  </button>
                  <button onClick={handleSaveUploads} disabled={uploading}
                    className="flex items-center gap-2 text-white px-4 py-2 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
                    style={{ background: "#7c3aed" }}>
                    <i className="ri-upload-2-line" />Upload {pendingFiles.length} File{pendingFiles.length !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Main layout ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-2">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Categories</p>
              </div>
              <button onClick={() => setActiveCategory("all")}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-all cursor-pointer border-b border-gray-50"
                style={activeCategory === "all" ? { background: "#f5f3ff", color: "#7c3aed", fontWeight: 600 } : { color: "#4b5563" }}>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-apps-2-line text-base" /></span>
                  All Media
                </div>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">{mediaFiles.length}</span>
              </button>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <button key={key} onClick={() => setActiveCategory(key as MediaCategory)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm transition-all cursor-pointer border-b border-gray-50 last:border-0"
                  style={activeCategory === key ? { background: "#f5f3ff", color: "#7c3aed", fontWeight: 600 } : { color: "#4b5563" }}>
                  <div className="flex items-center gap-2.5">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cfg.icon} text-base`} style={{ color: activeCategory === key ? "#7c3aed" : cfg.color }} />
                    </span>
                    {cfg.label}
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">{categoryCounts[key] || 0}</span>
                </button>
              ))}
            </div>

            {/* Storage bars */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-700 mb-3">Files by Category</p>
              <div className="space-y-2">
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                  const count = categoryCounts[key] || 0;
                  const pct   = mediaFiles.length > 0 ? Math.round((count / mediaFiles.length) * 100) : 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-gray-500">{cfg.label}</span>
                        <span className="font-medium text-gray-700">{count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: cfg.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3 space-y-4">

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Total Files", value: mediaFiles.length,                                             icon: "ri-folder-3-line",  color: "#7c3aed" },
                { label: "Images",      value: mediaFiles.filter((f) => f.type.startsWith("image/")).length,  icon: "ri-image-line",     color: "#10b981" },
                { label: "Documents",   value: mediaFiles.filter((f) => !f.type.startsWith("image/")).length, icon: "ri-file-text-line", color: "#f59e0b" },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.color + "18" }}>
                    <i className={`${s.icon} text-base`} style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Image grid with native drag-and-drop + bulk select */}
            {imageFiles.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 text-sm">Images ({imageFiles.length})</h3>
                  <div className="flex items-center gap-3">
                    {selectionMode && (
                      <button onClick={() => {
                        const allImgIds = new Set(imageFiles.map((f) => f.id));
                        const allSelected = imageFiles.every((f) => selectedIds.has(f.id));
                        if (allSelected) {
                          setSelectedIds((prev) => { const n = new Set(prev); allImgIds.forEach((id) => n.delete(id)); return n; });
                        } else {
                          setSelectedIds((prev) => { const n = new Set(prev); allImgIds.forEach((id) => n.add(id)); return n; });
                        }
                      }}
                        className="text-xs text-violet-600 hover:underline cursor-pointer whitespace-nowrap">
                        {imageFiles.every((f) => selectedIds.has(f.id)) ? "Deselect all" : "Select all images"}
                      </button>
                    )}
                    {!selectionMode && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-drag-move-2-line" /></span>
                        Drag to reorder
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                  {imageFiles.map((f) => (
                    <ImageCard
                      key={f.id}
                      file={f}
                      onPreview={setLightbox}
                      onDelete={handleDelete}
                      isSelected={selectedIds.has(f.id)}
                      onToggleSelect={toggleSelect}
                      selectionMode={selectionMode}
                      isDragging={dragId === f.id}
                      isDragOver={dragOverId === f.id}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Documents list */}
            {docFiles.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">Documents ({docFiles.length})</h3>
                  {selectionMode && (
                    <button onClick={() => {
                      const allDocIds = new Set(docFiles.map((f) => f.id));
                      const allSelected = docFiles.every((f) => selectedIds.has(f.id));
                      if (allSelected) setSelectedIds((prev) => { const n = new Set(prev); allDocIds.forEach((id) => n.delete(id)); return n; });
                      else setSelectedIds((prev) => { const n = new Set(prev); allDocIds.forEach((id) => n.add(id)); return n; });
                    }}
                      className="text-xs text-violet-600 hover:underline cursor-pointer whitespace-nowrap">
                      {docFiles.every((f) => selectedIds.has(f.id)) ? "Deselect all" : "Select all docs"}
                    </button>
                  )}
                </div>
                <div className="divide-y divide-gray-50">
                  {docFiles.map((f) => {
                    const catCfg = CATEGORY_CONFIG[f.category];
                    return (
                      <div key={f.id}
                        className={`flex items-center gap-4 px-5 py-3 transition-all group ${selectionMode ? "cursor-pointer hover:bg-violet-50" : "hover:bg-gray-50"} ${selectedIds.has(f.id) ? "bg-violet-50" : ""}`}
                        onClick={() => selectionMode && toggleSelect(f.id)}>
                        {selectionMode && (
                          <div className={`w-5 h-5 flex items-center justify-center rounded-md border-2 flex-shrink-0 transition-all ${selectedIds.has(f.id) ? "bg-violet-500 border-violet-500" : "border-gray-300"}`}>
                            {selectedIds.has(f.id) && <i className="ri-check-line text-white text-xs" />}
                          </div>
                        )}
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: catCfg?.bg || "#f8fafc" }}>
                          <i className={`${catCfg?.icon || "ri-file-line"} text-lg`} style={{ color: catCfg?.color || "#64748b" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{f.name}</p>
                          <p className="text-xs text-gray-400">{f.size} · {catCfg?.label || f.category} · {new Date(f.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                        </div>
                        {!selectionMode && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a href={f.url} download={f.name}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 text-amber-600 cursor-pointer transition-all">
                              <i className="ri-download-line text-sm" />
                            </a>
                            <button onClick={() => handleDelete(f.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-400 cursor-pointer transition-all">
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Loading skeleton */}
            {loadingMedia && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="rounded-xl bg-gray-100 animate-pulse" style={{ aspectRatio: "1" }} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loadingMedia && filtered.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-100 text-center py-16 text-gray-400">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gray-100 mx-auto mb-4">
                  <i className="ri-image-line text-3xl" />
                </div>
                <p className="text-sm font-medium text-gray-600">No media files found</p>
                <p className="text-xs mt-1">{search ? `No results for "${search}"` : "Upload some files to get started"}</p>
                {!search && (
                  <button onClick={() => setShowUploadPanel(true)}
                    className="mt-4 px-4 py-2 text-white text-sm rounded-xl font-medium cursor-pointer transition-all"
                    style={{ background: "#7c3aed" }}>
                    Upload Media
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
